import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { DropdownItemAction } from 'reactApp/ui/DropdownItemAction/DropdownItemAction';
import { DropdownFont, DropdownList, DropdownTheme } from 'reactApp/ui/DropdownList/DropdownList';
import { Hint } from 'reactApp/ui/Hint/Hint';
import { IToolbarItemProps, TToolbarDropdownItem } from 'reactApp/ui/Toolbar/Toolbar.types';
import { findDropdownPosition } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem.utils';
import { ChevronDownIcon } from 'reactApp/ui/VKUIIcons';
import { noopVoid } from 'reactApp/utils/helpers';

import styles from './ToolbarItem.css';

export const ToolbarItem = memo(
    ({
        id,
        text = '',
        hint = '',
        collapse = false,
        icon,
        list,
        withoutDropdownIcon,
        dropdownTheme,
        dropdownPosition,
        openDropdownByArrow = false,
        onClick = noopVoid,
        parentRef,
        mod,
    }: IToolbarItemProps): ReactElement => {
        const rootRef = useRef<HTMLDivElement>(null);

        const [isOpen, setIsOpen] = useState<boolean>(false);

        const renderDropdownItem = useCallback(
            ({ text, active, icon, mod, hotkeyLabel, asc, withStatus }: TToolbarDropdownItem) => (
                <DropdownItemAction
                    text={text}
                    active={active}
                    icon={icon}
                    mod={mod}
                    hotkey={hotkeyLabel}
                    asc={asc}
                    withStatus={withStatus}
                />
            ),
            []
        );

        const content = useMemo(
            () => (
                <div className={styles.content}>
                    {icon && <div className={styles.icon}>{icon}</div>}
                    {text && !collapse && <div className={styles.text}>{text}</div>}
                </div>
            ),
            [collapse, icon, text]
        );

        const closeDropdown = useCallback(() => {
            setIsOpen(false);
        }, []);

        const onItemClick = useCallback(() => {
            if (list && !openDropdownByArrow) {
                setIsOpen(!isOpen);
            }

            onClick();
        }, [isOpen, list, onClick, openDropdownByArrow]);

        const onArrowClick = useCallback(
            (event) => {
                if (!openDropdownByArrow) {
                    return;
                }

                event.stopPropagation();
                setIsOpen(!isOpen);
            },
            [isOpen, openDropdownByArrow]
        );

        return (
            <div
                className={classNames(styles.root, styles[`root_${id}`], {
                    [styles.root_active]: isOpen,
                    [styles.root_collapsed]: collapse || !text,
                    [styles.root_withDropdown]: list && !withoutDropdownIcon,
                    [styles[`root_${mod}`]]: !!mod,
                })}
                ref={rootRef}
                onClick={onItemClick}
                data-name={id}
            >
                {hint || collapse ? <Hint text={collapse ? text : hint}>{content}</Hint> : content}
                {list && (
                    <>
                        {!withoutDropdownIcon && (
                            <div
                                className={classNames(styles.dropdownIcon, { [styles.dropdownIcon_bordered]: openDropdownByArrow })}
                                onClick={onArrowClick}
                            >
                                <ChevronDownIcon />
                            </div>
                        )}
                        {isOpen && (
                            <DropdownList
                                list={list}
                                renderItem={renderDropdownItem}
                                close={closeDropdown}
                                calcPosition={findDropdownPosition({ ref: rootRef, parentRef, position: dropdownPosition })}
                                theme={dropdownTheme || DropdownTheme.small}
                                parentRef={rootRef}
                                gaId="toolbar"
                                closeOnResize
                                closeOnScroll
                                font={DropdownFont.mailSans}
                                animated
                            />
                        )}
                    </>
                )}
            </div>
        );
    }
);

ToolbarItem.displayName = 'NewToolbarItem';
